<template>
  <div class="mx-auto flex w-full max-w-2xl flex-col">
    <div v-if="currentStep == 'setup'" class="flex flex-col gap-4">
      <div class="mb-4 flex flex-col">
        <h3 class="text-display-medium">Starte in deinem Team mit dem Entwicklungsdialog</h3>
        <p class="text-body-large text-on-surface-variant mt-4">
          Mit wem möchtest du einen Dialog führen?
        </p>
      </div>
      <BaseCard class="flex flex-col p-4">
        <BaseMemberSelect
          v-model="selectedMemberIds"
          :required="true"
          :hide-list="true"
          :error="errors.get('members')"
          class="bg-surface-container"
        />
        <div v-if="selectedMembers.length > 0" class="flex flex-col">
          <h3 class="text-title-medium text-on-surface-variant my-2">
            {{ $t('teams.members') }}
          </h3>
          <div class="flex flex-col gap-2">
            <div
              v-for="member of selectedMembers"
              :key="member.id"
              class="flex flex-row items-center"
            >
              <BaseMemberAvatar :member="member" />
              <div class="ml-3 flex grow flex-col">
                <span class="text-title-medium -mb-1">{{ memberShortName(member) }}</span>
                <span class="text-body-medium text-on-surface-variant">{{
                  memberSecondaryInformation(member) ?? $t('common.pending')
                }}</span>
              </div>
              <BaseIconButton
                icon="remove"
                color="outlined"
                @click="() => removeMember(member.id)"
              />
            </div>
          </div>
        </div>
      </BaseCard>
    </div>
    <div v-if="currentStep == 'settings'" class="w-full">
      <div class="mb-4 flex flex-row items-center">
        <div class="relative mr-2 flex h-20 w-20 shrink-0 items-center justify-center">
          <span class="text-display-medium text-on-primary-container relative z-10">2</span>
          <BaseShape
            shape="7-sided-cookie"
            class="text-primary-container absolute inset-0 z-0 h-full w-full"
          />
        </div>
        <h3 class="text-title-large">Gib deinem Team einen Namen</h3>
      </div>
    </div>
    <div v-if="currentFeedbackTopic" class="w-full">
      <FeedbackTopicHero :feedback-topic="currentFeedbackTopic" class="mb-2" />
      <FeedbackTopicFormItem
        :feedback-topic="currentFeedbackTopic"
        :task="formData.tasks?.find((task) => task.feedbackTopicId == currentFeedbackTopic!.id)"
        :error="errors.get(currentFeedbackTopic.id)"
        @toggled="toggleFeedbackTopicTask(currentFeedbackTopic.id, currentFeedbackTopic.type)"
      />
    </div>
    <div v-if="currentStep == 'notification'"></div>
    <div
      class="bg-surface-container-low relative mt-20 flex w-full flex-row justify-between rounded-full p-3"
    >
      <BaseTextButton v-if="!isFirstStep" @click="previousStep" size="M" color="text"
        >Zurück</BaseTextButton
      >
      <div
        class="absolute top-1/2 left-1/2 flex w-1/3 -translate-x-1/2 -translate-y-1/2 flex-row gap-1"
      >
        <div
          v-for="(step, index) in steps"
          class="h-2 shrink basis-full rounded-full"
          :class="[
            index < currentStepIndex && 'bg-secondary-container',
            index > currentStepIndex && 'bg-surface-container-highest',
            step == currentStep && 'bg-secondary',
          ]"
        ></div>
      </div>
      <BaseTextButton v-if="!isLastStep" @click="nextStep" size="M" color="filled" class="ml-auto"
        >Weiter</BaseTextButton
      >
      <BaseTextButton v-else @click="addCoachees" size="M" color="filled"
        >{{ $t('coachees.inviteAction') }}</BaseTextButton
      >
    </div>
  </div>
</template>
<script lang="ts" setup>
  import BaseCard from '../../../core/components/base/card/BaseCard.vue'
  import BaseMemberSelect from '../../../core/components/base/member-select/BaseMemberSelect.vue'
  import { useEventBus } from '@vueuse/core'
  import { useI18n } from 'vue-i18n'
  import usePost from '../../../core/composables/api/usePost'
  import { EventKey } from '../../../core/events/event-key'
  import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
  import { FeedbackTopicType } from '@/core/models/feedback-topic'
  import { useSnackStore } from '../../../stores/snack.store'
  import BaseMemberAvatar from '../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseShape from '../../../core/components/base/shapes/BaseShape.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { useRouter } from 'vue-router'
  import {
    type JournalTask,
    useAddCoacheesWizzard,
    type StoreableCoachees,
  } from '../composables/useAddCoacheesWizzard'
  import type { CompleteFeedbackTopic } from '../../../core/composables/use-complete-feedback-topics'
  import BaseIconButton from '../../../core/components/base/button/icon-button/BaseIconButton.vue'
  import FeedbackTopicFormItem from '../components/FeedbackTopicFormItem.vue'
  import FeedbackTopicHero from '../components/FeedbackTopicHero.vue'
import { memberSecondaryInformation, memberShortName } from '@/core/models/member/member-name.ts'

  interface Props {
    feedbackTopics: ReadonlyArray<CompleteFeedbackTopic>
  }

  const props = defineProps<Props>()

  const snackStore = useSnackStore()
  const { t } = useI18n()
  const bus = useEventBus(EventKey.coacheeInvited)
  const router = useRouter()
  const {
    mutate,
    status: saveStatus,
    data: savedData,
  } = usePost<string, StoreableCoachees>('coaching/coachees')

  const {
    formData,
    selectedMemberIds,
    errors,
    removeMember,
    clearForm,
    selectedMembers,
    toggleFeedbackTopicTask,
    nextStep,
    previousStep,
    currentStep,
    currentFeedbackTopic,
    isLastStep,
    isFirstStep,
    steps,
    currentStepIndex,
  } = useAddCoacheesWizzard(props.feedbackTopics)

  const addCoachees = async () => {
    if (errors.value.size == 0) {
      const tasks =
        formData.tasks
          ?.filter((task) => task.isActive)
          .map((task) => {
            if (task.feedbackTopicType !== FeedbackTopicType.Journal) {
              return task
            }

            const journalTask = task as JournalTask
            return {
              ...journalTask,
              recurrenceFrequency: journalTask.isRecurring
                ? journalTask.recurrenceFrequency
                : undefined,
              recurrenceInterval: journalTask.isRecurring ? journalTask.recurrenceInterval : undefined,
            }
          }) ?? []

      await mutate({
        coacheeIds: formData.coacheeIds!,
        tasks,
      })
    }

    switch (saveStatus.value) {
      case ApiRequestStatus.Success:
        snackStore.showInfo(t('coachees.create-success'))
        clearForm()
        bus.emit({ coacheeId: savedData.value })
        router.push({ name: 'coachees' })
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('coachees.create-error'))
        break
    }
  }
</script>
